<template>
  <div class="card border-0 bg-white profile-settings-card rounded-0">
    <div class="card-body p-15 p-sm-20 p-md-25 p-lg-30 letter-spacing">
      <form v-on:submit="submit">
        <div class="row">
          <!-- CENTER_ID -->
          <div class="col-md-6" v-if="!update && (centers ?? []).length > 1">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                {{ $t('page.center') }}*
              </label>
              <div>
                <select class="form-select shadow-none text-black fs-md-15" v-model="patient.center_id" required
                  :class="[{ 'is-invalid': submitted && errors?.center_id }]" :disabled="update">
                  <option v-for="center in centers" :key="center.id" :value="center.id">{{ center.name }}</option>
                </select>
              </div>
              <!-- ERRORS -->
              <div class="form-messages mt-2 px-1" v-if="errors?.sex ?? false">
                <p v-for="error in errors.sex" :key="error" class="small text-danger lh-1 m-0 mt-1">{{
                  error }}</p>
              </div>
            </div>
          </div>
          <!-- ANAGRAPH -->
          <div class="col-12">
            <h4 class="mb-15 mb-sm-20 mb-md-25">{{ $t('form.section.patient_information') }}</h4>
          </div>
          <!-- FIRST NAME -->
          <div class="col-md-6">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                {{ $t('form.first_name.label') }}*
              </label>
              <input v-model="patient.firstname" type="text" class="form-control shadow-none text-black" required
                :class="[{ 'is-invalid': submitted && errors?.firstname }]"
                :placeholder="$t('form.first_name.placeholder')" />
              <!-- ERRORS -->
              <div class="form-messages mt-2 px-1" v-if="errors?.firstname ?? false">
                <p v-for="error in errors.firstname" :key="error" class="small text-danger lh-1 m-0 mt-1">{{
                  error }}</p>
              </div>
            </div>
          </div>
          <!-- LAST NAME -->
          <div class="col-md-6">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                {{ $t('form.last_name.label') }}*
              </label>
              <input v-model="patient.lastname" type="text" class="form-control shadow-none text-black" required
                :class="[{ 'is-invalid': submitted && errors?.lastname }]"
                :placeholder="$t('form.last_name.placeholder')" />
              <!-- ERRORS -->
              <div class="form-messages mt-2 px-1" v-if="errors?.lastname ?? false">
                <p v-for="error in errors.lastname" :key="error" class="small text-danger lh-1 m-0 mt-1">{{
                  error }}</p>
              </div>
            </div>
          </div>
          <!-- BIRTH DATE -->
          <div class="col-md-6">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                {{ $t('form.birth_date.label') }}*
              </label>
              <input v-model="patient.birthdate" type="date" class="form-control shadow-none text-black" required
                :class="[{ 'is-invalid': submitted && errors?.birthdate }]">
              <!-- ERRORS -->
              <div class="form-messages mt-2 px-1" v-if="errors?.birthdate ?? false">
                <p v-for="error in errors.birthdate" :key="error" class="small text-danger lh-1 m-0 mt-1">{{
                  error }}</p>
              </div>
            </div>
          </div>
          <div class="col-12"></div>
          <!-- SEX -->
          <div class="col-md-6">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                {{ $t('form.gender.label') }}
              </label>
              <div>
                <select class="form-select shadow-none text-black fs-md-15" v-model="patient.sex"
                  :class="[{ 'is-invalid': submitted && errors?.sex }]">
                  <option v-for="gender_id in available_genders" :key="gender_id" :value="gender_id.toString()">{{
                    $t(`form.gender.${gender_id}`) }}</option>
                </select>
              </div>
              <!-- ERRORS -->
              <div class="form-messages mt-2 px-1" v-if="errors?.sex ?? false">
                <p v-for="error in errors.sex" :key="error" class="small text-danger lh-1 m-0 mt-1">{{
                  error }}</p>
              </div>
            </div>
          </div>
          <div class="col-12"></div>
          <!-- ADDRESS -->
          <div class="col-md-6">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                {{ $t('form.address.label') }}*
              </label>
              <input v-model="patient.address" class="form-control shadow-none text-black" required
                :class="[{ 'is-invalid': submitted && errors?.address }]"
                :placeholder="$t('form.address.placeholder')" />
              <!-- ERRORS -->
              <div class="form-messages mt-2 px-1" v-if="errors?.address ?? false">
                <p v-for="error in errors.address" :key="error" class="small text-danger lh-1 m-0 mt-1">{{
                  error }}</p>
              </div>
            </div>
          </div>
          <!-- CITY -->
          <div class="col-md-6">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                {{ $t('form.city.label') }}*
              </label>
              <input v-model="patient.city" class="form-control shadow-none text-black"
                :placeholder="$t('form.city.placeholder')" required
                :class="[{ 'is-invalid': submitted && errors?.city }]" />
              <!-- ERRORS -->
              <div class="form-messages mt-2 px-1" v-if="errors?.city ?? false">
                <p v-for="error in errors.city" :key="error" class="small text-danger lh-1 m-0 mt-1">{{
                  error }}</p>
              </div>
            </div>
          </div>
          <!-- REGION -->
          <div class="col-md-6">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                {{ $t('form.region.label') }}
              </label>
              <input v-model="patient.region" class="form-control shadow-none text-black"
                :class="[{ 'is-invalid': submitted && errors?.region }]" :placeholder="$t('form.region.placeholder')" />
              <!-- ERRORS -->
              <div class="form-messages mt-2 px-1" v-if="errors?.region ?? false">
                <p v-for="error in errors.region" :key="error" class="small text-danger lh-1 m-0 mt-1">{{
                  error }}</p>
              </div>
            </div>
          </div>
          <!-- ZIP CODE -->
          <div class="col-md-6">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                {{ $t('form.zip_code.label') }}
              </label>
              <input v-model="patient.zipcode" class="form-control shadow-none text-black"
                :class="[{ 'is-invalid': submitted && errors?.zipcode }]"
                :placeholder="$t('form.zip_code.placeholder')" />
              <!-- ERRORS -->
              <div class="form-messages mt-2 px-1" v-if="errors?.zipcode ?? false">
                <p v-for="error in errors.zipcode" :key="error" class="small text-danger lh-1 m-0 mt-1">{{
                  error }}</p>
              </div>
            </div>
          </div>
          <!-- COUNTRY -->
          <div class="col-md-6">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                {{ $t('form.country.label') }}*
              </label>
              <country-select v-model="patient.country" :country="patient.country"
                class="form-select shadow-none text-black fs-md-15" :placeholder="$t('form.country.placeholder')"
                :class="[{ 'is-invalid': submitted && errors?.country }]" required
                whiteList="['IT','GB','FR','ES','DE']" />
              <!-- ERRORS -->
              <div class="form-messages mt-2 px-1" v-if="errors?.country ?? false">
                <p v-for="error in errors.country" :key="error" class="small text-danger lh-1 m-0 mt-1">{{
                  error }}</p>
              </div>
            </div>
          </div>
          <!-- EXTRAS -->
          <!-- <template v-if="extra_definitions !== undefined && extra_responses !== undefined">
            <ExtraDefinitions :extra_definitions="extra_definitions" v-model:extra_responses="extra_responses"
              :submitted="submitted" :errors="errors"></ExtraDefinitions>
          </template> -->

          <!-- CONTACTS -->
          <div class="col-12">
            <h4 class="mb-15 mb-sm-20 mb-md-25">{{ $t('form.section.contacts') }}</h4>
          </div>
          <!-- UPDATE (DISABLED) -->
          <template v-if="update">
            <!-- EMAIL -->
            <div class="col-md-6">
              <div class="form-group mb-15 mb-sm-20 mb-md-25">
                <label class="d-block text-black fw-semibold mb-10">
                  {{ $t('form.email.label') }}*
                </label>
                <input v-model="patient.users![0].email" type="email" class="form-control shadow-none text-black"
                  required :class="[{ 'is-invalid': submitted && errors?.email }]" pattern="[^@\s]+@[^@\s]+\.[^@\s]+"
                  :placeholder="$t('form.email.placeholder')" disabled />
              </div>
            </div>
            <!-- PHONE -->
            <div class="col-md-6">
              <div class="form-group mb-15 mb-sm-20 mb-md-25">
                <label class="d-block text-black fw-semibold mb-10">
                  {{ $t('form.phone.label') }}*
                </label>
                <input v-model="patient.users![0].phone_number" type="text" class="form-control shadow-none text-black"
                  :class="[{ 'is-invalid': submitted && errors?.phone_number }]" required
                  :placeholder="$t('form.phone.placeholder')" disabled />
                <!-- ERRORS -->
                <div class="form-messages mt-2 px-1" v-if="errors?.phone_number ?? false">
                  <p v-for="error in errors.phone_number" :key="error" class="small text-danger lh-1 m-0 mt-1">{{
                    error }}</p>
                </div>
              </div>
            </div>
          </template>
          <!-- CREATE -->
          <template v-else>
            <!-- EMAIL -->
            <div class="col-md-6">
              <div class="form-group mb-15 mb-sm-20 mb-md-25">
                <label class="d-block text-black fw-semibold mb-10">
                  {{ $t('form.email.label') }}*
                </label>
                <input v-model="patient.email" type="email" class="form-control shadow-none text-black"
                  :class="[{ 'is-invalid': submitted && errors?.email }]" :required="!patient.caregiver_account"
                  pattern="[^@\s]+@[^@\s]+\.[^@\s]+" :placeholder="$t('form.email.placeholder')" />
                <!-- ERRORS -->
                <div class="form-messages mt-2 px-1" v-if="errors?.email ?? false">
                  <p v-for="error in errors.email" :key="error" class="small text-danger lh-1 m-0 mt-1">{{
                    error }}</p>
                </div>
              </div>
            </div>
            <!-- PHONE -->
            <div class="col-md-6">
              <div class="form-group mb-15 mb-sm-20 mb-md-25">
                <label class="d-block text-black fw-semibold mb-10">
                  {{ $t('form.phone.label') }}*
                </label>
                <input v-model="patient.phone_number" type="text" class="form-control shadow-none text-black"
                  :class="[{ 'is-invalid': submitted && errors?.phone_number }]" :required="!patient.caregiver_account"
                  :placeholder="$t('form.phone.placeholder')" />
                <!-- ERRORS -->
                <div class="form-messages mt-2 px-1" v-if="errors?.phone_number ?? false">
                  <p v-for="error in errors.phone_number" :key="error" class="small text-danger lh-1 m-0 mt-1">{{
                    error }}</p>
                </div>
              </div>
            </div>
          </template>


          <!-- CAREGIVER -->
          <template v-if="!update">
            <!-- ADD CAREGIVER -->
            <div class="col-12">
              <div class="form-check form-group mb-15 mb-sm-20 mb-md-25">
                <input class="form-check-input shadow-none" type="checkbox" v-model="patient.caregiver_account"
                  v-on:change="resetCaregiver" id="caregiver">
                <label class="form-check-label fw-medium position-relative top-1" for="caregiver">
                  {{ $t('form.add_caregiver.label') }}
                </label>
              </div>
            </div>
            <template v-if="patient.caregiver_account">
              <!-- CAREGIVER INFO -->
              <div class="col-12">
                <h4 class="mb-15 mb-sm-20 mb-md-25">{{ $t('form.section.caregiver_information') }}</h4>
              </div>
              <!-- FIRST NAME -->
              <div class="col-md-6">
                <div class="form-group mb-15 mb-sm-20 mb-md-25">
                  <label class="d-block text-black fw-semibold mb-10">
                    {{ $t('form.first_name.label') }}*
                  </label>
                  <input v-model="patient.caregiver_firstname" :required="patient.caregiver_account" type="text"
                    :class="[{ 'is-invalid': submitted && errors?.caregiver_firstname }]"
                    class="form-control shadow-none text-black" :placeholder="$t('form.first_name.placeholder')" />
                  <!-- ERRORS -->
                  <div class="form-messages mt-2 px-1" v-if="errors?.caregiver_firstname ?? false">
                    <p v-for="error in errors.caregiver_firstname" :key="error" class="small text-danger lh-1 m-0 mt-1">
                      {{
                        error }}</p>
                  </div>
                </div>
              </div>
              <!-- LAST NAME -->
              <div class="col-md-6">
                <div class="form-group mb-15 mb-sm-20 mb-md-25">
                  <label class="d-block text-black fw-semibold mb-10">
                    {{ $t('form.last_name.label') }}*
                  </label>
                  <input v-model="patient.caregiver_lastname" :required="patient.caregiver_account" type="text"
                    :class="[{ 'is-invalid': submitted && errors?.caregiver_lastname }]"
                    class="form-control shadow-none text-black" :placeholder="$t('form.last_name.placeholder')" />
                  <!-- ERRORS -->
                  <div class="form-messages mt-2 px-1" v-if="errors?.caregiver_lastname ?? false">
                    <p v-for="error in errors.caregiver_lastname" :key="error" class="small text-danger lh-1 m-0 mt-1">
                      {{
                        error }}</p>
                  </div>
                </div>
              </div>
              <!-- EMAIL -->
              <div class="col-md-6">
                <div class="form-group mb-15 mb-sm-20 mb-md-25">
                  <label class="d-block text-black fw-semibold mb-10">
                    {{ $t('form.email.label') }}*
                  </label>
                  <input v-model="patient.caregiver_email" :required="patient.caregiver_account"
                    :class="[{ 'is-invalid': submitted && errors?.caregiver_email }]" pattern="[^@\s]+@[^@\s]+\.[^@\s]+"
                    type="email" class="form-control shadow-none text-black"
                    :placeholder="$t('form.email.placeholder')" />
                  <!-- ERRORS -->
                  <div class="form-messages mt-2 px-1" v-if="errors?.caregiver_email ?? false">
                    <p v-for="error in errors.caregiver_email" :key="error" class="small text-danger lh-1 m-0 mt-1">{{
                      error }}</p>
                  </div>
                </div>
              </div>
              <!-- PHONE -->
              <div class="col-md-6">
                <div class="form-group mb-15 mb-sm-20 mb-md-25">
                  <label class="d-block text-black fw-semibold mb-10">
                    {{ $t('form.phone.label') }}*
                  </label>
                  <input v-model="patient.caregiver_phone_number" :required="patient.caregiver_account" type="text"
                    :class="[{ 'is-invalid': submitted && errors?.caregiver_phone_number }]"
                    class="form-control shadow-none text-black" :placeholder="$t('form.phone.placeholder')" />
                  <!-- ERRORS -->
                  <div class="form-messages mt-2 px-1" v-if="errors?.caregiver_phone_number ?? false">
                    <p v-for="error in errors.caregiver_phone_number" :key="error"
                      class="small text-danger lh-1 m-0 mt-1">{{
                        error }}</p>
                  </div>
                </div>
              </div>
            </template>
          </template>

          <div class="col-12" v-if="loading">
            <SpinnerLoading />
          </div>
          <div class="col-md-12" v-else>
            <div class="d-flex align-items-center justify-content-between gap-3">
              <!-- DELETE -->
              <button v-if="update && destroyEnabled" type="button"
                class="bg-transparent p-0 border-0 text-danger lh-1 fw-medium" data-bs-toggle="modal"
                data-bs-target="#confirmModal">
                <i class="ph ph-trash lh-1 me-1 position-relative top-2"></i>
                <span class="position-relative">{{ $t('cta.delete_patient') }}</span>
              </button>
              <span v-else></span>
              <!-- SAVE -->
              <div class="d-flex flex-wrap gap-3 justify-content-end ms-auto">
                <DiscardButton @confirm="$router.push({ path: update ? `/patient/${patient.id}` : '/patients' })" />
                <button
                  class="default-btn transition fw-medium text-white pt-10 pb-10 ps-25 pe-25 pt-md-11 pb-md-11 ps-md-35 pe-md-35 fs-md-15 fs-lg-16"
                  type="submit" v-on:click="submitted = true">
                  {{ $t('cta.save_patient') }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  </div>
  <!-- ERROR MESSAGE -->
  <div class="row justify-content-center">
    <div class="col">
      <div
        class="alert alert-danger alert-dismissible fade border-0 border-danger-subtle border-top rounded-0 m-0 mb-25"
        role="alert" id="errorMessage">
        <strong>{{ $t('error.something_went_wrong') }}</strong>
        <button type="button" class="btn-close" v-on:click="hideError" aria-label="Close"></button>
      </div>
    </div>
  </div>
  <ConfirmModal @confirm="deletePatient()" :title="$t('cta.$delete.title')" :message="$t('cta.$delete.message')" />
</template>

<script lang="ts">
import { defineComponent } from "vue";
import ConfirmModal from "@/components/Widgets/ConfirmModal.vue";
import { CreatePatientInfo, PatientExtras, PatientExtraDefinitionValue, PatientExtraDefinition, PatientExtraResponses } from "@/utils/models/user.model";
import { removeNullProperties } from "@/utils/methods/remove_null_properties";
import API from "@/services/api/api.service";
import stateStore from "@/utils/store";
import SpinnerLoading from "@/components/Widgets/Spinner.vue";
import DiscardButton from "@/components/Widgets/DiscardButton.vue";
// import ExtraDefinitions from "./ExtraDefinitions.vue";
import config from "@/utils/const/config";

export default defineComponent({
  name: "PatientForm",
  components: {
    ConfirmModal,
    SpinnerLoading,
    DiscardButton,
    // ExtraDefinitions
  },
  data() {
    return {
      patient: this.$route.meta.patient ?? {} as CreatePatientInfo,
      submitted: false,
      loading: false,
      errors: undefined as any,
      extra_definitions: (this.$route.meta.extra_definitions ?? []) as PatientExtras[],
      extra_definition_values: (this.$route.meta.extra_definition_values ?? []) as PatientExtraDefinitionValue[],
      extra_responses: undefined,
    } as {
      patient: CreatePatientInfo,
      submitted: boolean,
      loading: boolean,
      errors?: any,
      extra_definitions?: PatientExtras[],
      extra_definition_values?: PatientExtraDefinitionValue[],
      extra_responses?: PatientExtraResponses,
    }
  },
  mounted() {
    this.setupExtraResponses();
    if (!this.update && this.centers?.length === 1) {
      this.patient.center_id = this.centers[0].id;
    }
  },
  computed: {
    update() {
      return this.$route.meta.patient !== undefined && this.$route.meta.patient !== null;
    },
    centers() {
      return stateStore.getCentersWithPermisison('store');
    },
    destroyEnabled(): boolean { return stateStore.getCenterPermission(this.patient.center_id, 'destroy') },
    available_genders(): number[] { return config.available_genders }
  },
  methods: {
    submit(event): void {
      this.errors = undefined;
      event.preventDefault();
      console.debug("PATIENT", this.patient);

      this.loading = true;

      if (this.update) {
        console.debug('UPDATE');
        removeNullProperties<CreatePatientInfo>(this.patient);
        API.updatePatient(this.patient).then((response) => {
          return this.updateExtras().catch((error) => {
            console.error('UPDATE_EXTRA_ERROR', error);
          }).finally(() => {
            this.$router.push(`/patient/${response.data.data.id}`);
          })
        }).catch((error) => {
          this.errors = error.response?.data?.errors;
          this.showError();
        }).finally(() => {
          this.loading = false
        });
      } else {
        console.debug('CREATE');
        if (this.patient.caregiver_account === true) {
          if (this.patient.email === '') {
            this.patient.email = undefined;
          }
          if (this.patient.phone_number === '') {
            this.patient.phone_number = undefined;
          }
        }
        this.patient.send_mail = true;
        API.createPatient(this.patient).then((response) => {
          return this.createExtras(response.data.data.id).catch((error) => {
            console.error('CREATE_EXTRA_ERROR', error);
          }).finally(() => {
            this.$router.push(`/patient/${response.data.data.id}`);
          })
        }).catch((error) => {
          this.errors = error.response?.data?.errors;
          this.showError();
        }).finally(() => {
          this.loading = false
        });
      }
    },
    deletePatient(): void {
      API.deletePatient(this.patient.id!).then((response) => {
        if (response.data.success === true) {
          this.$router.push('/patients');
        }
      }).catch((error) => {
        this.showError();
      }).finally(() => {
        this.loading = false;
        console.info("DELETED_PATIENT_ID", this.patient.id);
      })
    },
    resetCaregiver() {
      this.patient.caregiver_email = undefined;
      this.patient.caregiver_phone_number = undefined;
      this.patient.caregiver_firstname = undefined;
      this.patient.caregiver_lastname = undefined;
    },
    showError() {
      const error = document.getElementById('errorMessage');
      error?.classList.add('show');
      setTimeout(() => {
        this.hideError();
      }, 5000)
    },
    hideError() {
      const error = document.getElementById('errorMessage');
      error?.classList.remove('show');
    },
    setupExtraResponses() {
      this.extra_responses = {};
      this.extra_definitions?.forEach((extra) => {
        const definition_values = this.extra_definition_values?.find((def) => def.dataextra_schema_name === extra.schema_name);
        Object.defineProperty(this.extra_responses, extra.schema_name, {
          value: Object.fromEntries(extra.definitions.map((def) => {
            const default_value = def.default_value !== undefined && def.default_value !== '' ? def.default_value : undefined;
            return [def.name, definition_values?.dataextra_value![def.name] ?? default_value ?? undefined]
          })),
          writable: true
        });
      });
      console.debug('EXTRA_RESPONSES', this.extra_responses);
    },
    async createExtras(patient_id: string | number) {
      let values = this._computeExtraResponses();
      return Promise.all([...values.map((definition_value) => API.createExtraDefinitionValues({ entity: 'patients', entity_id: patient_id, definition_values: definition_value }))]);
    },
    async updateExtras() {
      try {
        console.debug('UPDATE_EXTRAS');
        // INITIALIZE API DATA
        let create_data = [] as PatientExtraDefinitionValue[];
        let update_data = [] as PatientExtraDefinitionValue[];
        // FOR EVERY RESPONSE
        Object.getOwnPropertyNames(this.extra_responses).forEach((schema_name) => {
          let stored_values = this.extra_definition_values?.find((def) => def.dataextra_schema_name === schema_name);
          // CHECK IF EXIST A STORED DATA FOR THIS SCHEMA
          if (stored_values === undefined) {
            // IF NOT EXIST CREATE A NEW ENTRY
            let definitions = removeNullProperties(this.extra_responses![schema_name]);
            if (Object.getOwnPropertyNames(definitions).length !== 0) {
              create_data.push({
                dataextra_schema_name: schema_name,
                dataextra_value: definitions
              } as PatientExtraDefinitionValue);
            }
          } else {
            // IF EXIST UPDATE VALUE
            update_data.push({
              dataextra_schema_name: schema_name,
              dataextra_value: this.extra_responses![schema_name]
            } as PatientExtraDefinitionValue);
          }
        });

        console.debug('CREATE_EXTRA_DEFINITIONS', create_data);
        console.debug('UPDATE_EXTRA_DEFINITIONS', update_data);
        return Promise.all([
          create_data.map((definition_value) => API.createExtraDefinitionValues({ entity: 'patients', entity_id: this.patient!.id!, definition_values: definition_value })),
          update_data.map((definition_value) => API.updateExtraDefinitionValues({ entity: 'patients', entity_id: this.patient!.id!, definition_values: definition_value }))
        ]);
      } catch (error) {
        throw error;
      }
    },
    _computeExtraResponses(): PatientExtraDefinitionValue[] {
      let stored = [] as PatientExtraDefinitionValue[]
      this.extra_definitions?.forEach((extra) => {
        let store = {} as PatientExtraDefinitionValue;
        // store.entity_id = this.patient.id!;
        store.dataextra_schema_name = extra.schema_name;
        store.dataextra_value = removeNullProperties(this.extra_responses![extra.schema_name]);
        if (Object.getOwnPropertyNames(store.dataextra_value).length !== 0) {
          stored.push(store);
        }
      });
      return stored;
    },
    _checkExtraDefinitionIsRequired(schema_name: string, definition: PatientExtraDefinition): boolean {
      let required = false;
      if (definition.rules?.required === true) {
        required = true;
      }
      if (definition.rules?.is_filled !== undefined && (definition.rules?.is_filled in this.extra_responses![schema_name])) {
        let value = this.extra_responses![schema_name][definition.rules.is_filled!];
        required = !(value === undefined || (value !== undefined && value === ''));
      }
      return required;
    },
  },
});

</script>